import {
  faCircleCheck,
  faEnvelope,
  faPrint
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useSearchParams } from "react-router-dom";

import { priceFormatter } from "~/utils/price-formatter";
import { T } from "@repo/transifex";
import { useOrder } from "~/hooks/queries/orders";
import { useCountdown } from "~/hooks/use-countdown";
import { useCashDrawerStatus } from "~/providers/app";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";

import { Button } from "../Button";
import { Dialog } from "../Dialog";
import { PrintButton } from "../pos/print/print-button";

type DialogPaymentSuccessProps = {
  orderId: string;
  change?: number;
  isAttended: boolean;
  onClose: () => void;
  onTimeout: () => void;
  receiptList?: string[];
};

export const DialogPaymentSuccess = ({
  onClose,
  orderId,
  isAttended,
  change = 0,
  receiptList,
  onTimeout
}: DialogPaymentSuccessProps) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const serviceId = searchParams.get("serviceId");

  const { data: till } = useCurrentTill();
  const { locale, currency } = useCurrentTillLocalisation();
  const { data: order, status } = useOrder(orderId);

  const cashDrawerStatus = useCashDrawerStatus();

  const { timeLeftInSeconds } = useCountdown({
    countdownDurationInSeconds: 10,
    onCountdownFinished: onTimeout,
    disabled: isAttended
  });

  const canCloseDialog =
    !till?.moduleConfig.requiredCashDrawerClosing ||
    cashDrawerStatus === "closed";

  if (status !== "success" || !order) return null;

  return (
    <Dialog
      backdropVariant="success"
      onOutsideClick={() => {
        canCloseDialog && onClose();
      }}
      size="md"
    >
      <div className="flex flex-col gap-y-10">
        <div className="flex flex-col gap-y-10 px-5 pt-8">
          <div className="flex flex-col items-center justify-center gap-y-5 text-primary-main">
            <FontAwesomeIcon className="h-11" icon={faCircleCheck} />

            <span className="text-3xl font-semibold">
              {order?.totalPriceUnit >= 0 ? (
                <T _str="Payment completed" />
              ) : (
                <T _str="Refund completed" />
              )}
            </span>
          </div>

          {change > 0 && (
            <p className="mb-4 text-center text-xl text-text-primary">
              <T
                _str="Change money {change}"
                change={priceFormatter({
                  value: change * 100,
                  currency,
                  locale
                })}
              />
            </p>
          )}

          {order.totalPriceUnit < 0 && (
            <p className="mb-4 text-center text-xl text-text-primary">
              <T
                _str="Refund {change}"
                change={priceFormatter({
                  value: -order.totalPriceUnit,
                  currency,
                  locale
                })}
              />
            </p>
          )}
        </div>

        <div className="px-5 pb-5">
          <div className="flex flex-col gap-y-5">
            <PrintButton
              orderId={orderId}
              icon={faPrint}
              receiptList={receiptList}
              size="large"
              className="text-lg"
              disabled={!canCloseDialog}
            />

            <Button
              className="text-lg"
              disabled={!canCloseDialog}
              icon={faEnvelope}
              onClick={() => {
                const query = serviceId ? `&serviceId=${serviceId}` : "";
                navigate({
                  pathname: "/pos/sell/email",
                  search: `?orderId=${orderId}${query}`
                });
              }}
              size="large"
              variant="secondary"
            >
              <T _str="Email receipt" />
            </Button>

            <Button
              className="text-lg"
              disabled={!canCloseDialog}
              onClick={onClose}
              size="large"
              variant="primary"
            >
              {isAttended ? (
                <T _str="Close" />
              ) : (
                <T _str="Close ({seconds})" seconds={timeLeftInSeconds} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
